import React, { useState, useEffect } from 'react';
import { getAccounts, createAccount, deleteAccount, resetAccountPassword, updateAccount, getRoles, createRole, updateRole, deleteRole } from '../services/api';

// Toutes les pages qu'un role peut ouvrir. Les quatre pages reservees a
// l'administrateur — Comptes, Logs, SMS, Parametres — n'y figurent pas : elles
// ne s'attribuent pas par role.
const ALL_PAGES = [
  { path: '/', label: 'Tableau de bord' },
  { path: '/dossier', label: 'Dossiers' },
  { path: '/cr', label: 'Comptes-rendus' },
  { path: '/email-log', label: 'Emails CR envoyés' },
  { path: '/planning', label: 'Planning' },
  { path: '/planification', label: 'Planification RDV' },
  { path: '/suivi-cri', label: 'Suivi CRI' },
  { path: '/suivi-terrain', label: 'Suivi Terrain' },
  { path: '/recherche', label: 'Recherche' },
  { path: '/emails', label: 'Boîte mail' },
  { path: '/sav', label: 'SAV Covage' },
  { path: '/commande-ont', label: 'Commande ONT' },
  { path: '/agenda', label: 'Agenda' },
  { path: '/pieces-jointes', label: 'Pièces jointes' },
  { path: '/workflow', label: 'Workflows' },
  { path: '/kpi', label: 'KPI Covage' },
  { path: '/qualifications', label: 'Qualifications SLA' },
  { path: '/import', label: 'Import Commandes' },
  { path: '/stats', label: 'Statistiques' },
  { path: '/techniciens', label: 'Techniciens' },
  { path: '/secteurs', label: 'Secteurs techniciens' },
  { path: '/absences', label: 'Absences' },
  { path: '/erdv', label: 'Créneaux E-RDV' },
  { path: '/remunerations', label: 'Rémunérations' },
  { path: '/stock-ont', label: 'Stock ONT' },
];

function RoleBadge({ name }) {
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: 'rgba(59,130,246,0.12)', color: '#3b82f6', border: '1px solid rgba(59,130,246,0.3)' }}>
      {name}
    </span>
  );
}

// ── Modal Rôle (créer ou modifier) ─────────────────────────────────────────
function RoleModal({ role, onClose, onSaved, onError }) {
  const [name, setName] = useState(role?.name || '');
  const [pages, setPages] = useState(role?.pages || []);
  const [saving, setSaving] = useState(false);

  const togglePage = p => setPages(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  const toggleAll = () => setPages(pages.length === ALL_PAGES.length ? [] : ALL_PAGES.map(p => p.path));

  const submit = async e => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (role?.id) await updateRole(role.id, { name, pages });
      else await createRole({ name, pages });
      onSaved();
    } catch (err) {
      onError(err.response?.data?.error || 'Erreur');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 540 }}>
        <div className="modal-header">
          <div className="modal-title">{role?.id ? 'Modifier le rôle' : 'Créer un rôle'}</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label className="form-label">Nom du rôle</label>
              <input className="form-input" value={name} onChange={e => setName(e.target.value)} required autoFocus placeholder="ex: Conducteur de travaux, Chef de conduite…" />
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <label className="form-label" style={{ margin: 0 }}>Pages accessibles ({pages.length}/{ALL_PAGES.length})</label>
                <button type="button" className="btn btn-secondary btn-sm" onClick={toggleAll}>
                  {pages.length === ALL_PAGES.length ? 'Tout décocher' : 'Tout cocher'}
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {ALL_PAGES.map(p => (
                  <label key={p.path} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, background: pages.includes(p.path) ? 'rgba(59,130,246,0.08)' : 'var(--bg3)', border: `1px solid ${pages.includes(p.path) ? 'rgba(59,130,246,0.3)' : 'var(--border)'}`, cursor: 'pointer', fontSize: 13, fontWeight: pages.includes(p.path) ? 600 : 400, transition: 'all .1s' }}>
                    <input type="checkbox" checked={pages.includes(p.path)} onChange={() => togglePage(p.path)} style={{ accentColor: '#3b82f6' }} />
                    {p.label}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving || !name.trim()}>
              {saving ? <span className="spinner" /> : role?.id ? 'Enregistrer' : 'Créer le rôle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Modal Créer / Modifier compte ──────────────────────────────────────────
function AccountModal({ account, roles, onClose, onSaved, onError }) {
  const isEdit = !!account?.id;
  const [form, setForm] = useState({
    login: account?.login || '',
    password: '',
    nom: account?.nom || '',
    prenom: account?.prenom || '',
    role_id: account?.role_id || account?.role || (roles[0]?.id || ''),
    telephone: account?.telephone || '',
    email: account?.email || '',
  });
  const [saving, setSaving] = useState(false);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        await updateAccount(account.id, { nom: form.nom, prenom: form.prenom, role_id: form.role_id, telephone: form.telephone, email: form.email });
      } else {
        await createAccount(form);
      }
      onSaved();
    } catch (err) {
      onError(err.response?.data?.error || 'Erreur');
      setSaving(false);
    }
  };

  const selectedRole = roles.find(r => r.id === form.role_id);

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <div className="modal-title">{isEdit ? `Modifier — ${account.prenom} ${account.nom}` : 'Créer un compte'}</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Prénom</label>
                <input className="form-input" value={form.prenom} onChange={set('prenom')} required autoFocus />
              </div>
              <div className="form-group">
                <label className="form-label">Nom</label>
                <input className="form-input" value={form.nom} onChange={set('nom')} required />
              </div>
            </div>
            {!isEdit && (
              <div className="form-group">
                <label className="form-label">Identifiant de connexion</label>
                <input className="form-input" value={form.login} onChange={set('login')} required placeholder="ex: jean.dupont" />
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Rôle</label>
              <select className="form-input form-select" value={form.role_id} onChange={set('role_id')} required>
                {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              {selectedRole && (
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                  {selectedRole.pages.length} page(s) accessible(s)
                </div>
              )}
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Tél. WhatsApp</label>
                <input className="form-input" value={form.telephone} onChange={set('telephone')} placeholder="+33 6 …" />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={form.email} onChange={set('email')} placeholder="prenom.nom@…" />
              </div>
            </div>
            {!isEdit && (
              <div className="form-group">
                <label className="form-label">Mot de passe</label>
                <input className="form-input" type="password" value={form.password} onChange={set('password')} required placeholder="Minimum 6 caractères" minLength={6} />
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <span className="spinner" /> : isEdit ? 'Enregistrer' : 'Créer le compte'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Modal Reset MDP ─────────────────────────────────────────────────────────
function ResetPasswordModal({ account, onClose, onDone, onError }) {
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async e => {
    e.preventDefault();
    setSaving(true);
    try { await resetAccountPassword(account.id, password); onDone(); }
    catch (err) { onError(err.response?.data?.error || 'Erreur'); setSaving(false); }
  };
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 380 }}>
        <div className="modal-header">
          <div className="modal-title">Changer le mot de passe</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <p style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 14 }}>
              Compte : <strong>{account.prenom} {account.nom}</strong> ({account.login})
            </p>
            <div className="form-group">
              <label className="form-label">Nouveau mot de passe</label>
              <input className="form-input" type="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder="Minimum 6 caractères" minLength={6} autoFocus />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? <span className="spinner" /> : 'Enregistrer'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Page principale ─────────────────────────────────────────────────────────
export default function Comptes() {
  const [tab, setTab] = useState('comptes');
  const [accounts, setAccounts] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const flash = (m, isErr = false) => {
    isErr ? setErr(m) : setMsg(m);
    setTimeout(() => isErr ? setErr('') : setMsg(''), 3500);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [aRes, rRes] = await Promise.all([getAccounts(), getRoles()]);
      setAccounts(aRes.accounts || []);
      setRoles(rRes.roles || []);
    } catch { flash('Erreur chargement', true); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const closeModal = () => setModal(null);
  const saved = m => { load(); flash(m); closeModal(); };

  const getRoleName = a => {
    const r = roles.find(x => x.id === (a.role_id || a.role));
    return r?.name || 'Conducteur';
  };

  return (
    <>
      <div className="topbar">
        <div>
          <div className="topbar-title">Comptes utilisateurs</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>Gestion des accès panel administrateur</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {tab === 'roles' && <button className="btn btn-secondary" onClick={() => setModal({ type: 'createRole' })}>+ Nouveau rôle</button>}
          {tab === 'comptes' && <button className="btn btn-primary" onClick={() => setModal({ type: 'createAccount' })}>+ Créer un compte</button>}
        </div>
      </div>

      <div className="page">
        {msg && <div className="alert alert-success" onClick={() => setMsg('')}>{msg} ✕</div>}
        {err && <div className="alert alert-error" onClick={() => setErr('')}>{err} ✕</div>}

        {/* Onglets */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
          {[['comptes', `Comptes (${accounts.length + 1})`], ['roles', `Rôles (${roles.length})`]].map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)} className="btn" style={{ background: tab === v ? 'var(--accent)' : 'var(--bg3)', color: tab === v ? '#fff' : 'var(--text2)', border: `1px solid ${tab === v ? 'var(--accent)' : 'var(--border)'}` }}>
              {l}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><span className="spinner" /></div>
        ) : tab === 'comptes' ? (
          <>
            {/* Compte admin */}
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--text3)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '.06em' }}>Compte administrateur</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', background: 'var(--bg3)', borderRadius: 10 }}>
                <div className="avatar" style={{ width: 38, height: 38, fontSize: 13, background: 'var(--accent)' }}>AD</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>Administrateur</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>login : admin · Accès complet à toutes les pages</div>
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: 'rgba(212,43,43,0.15)', color: '#D42B2B', border: '1px solid rgba(212,43,43,0.3)' }}>Admin</span>
              </div>
            </div>

            {/* Comptes créés */}
            <div className="card">
              <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--text3)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '.06em' }}>Comptes ({accounts.length})</div>
              {accounts.length === 0 ? (
                <div className="empty">
                  <div className="icon">👥</div>
                  <p>Aucun compte créé</p>
                  <button className="btn btn-primary btn-sm" onClick={() => setModal({ type: 'createAccount' })}>Créer le premier compte</button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {accounts.map(a => (
                    <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: 'var(--bg3)', borderRadius: 10 }}>
                      <div className="avatar" style={{ width: 38, height: 38, fontSize: 13, flexShrink: 0, background: '#3b82f6' }}>
                        {(a.prenom?.[0] || '?').toUpperCase()}{(a.nom?.[0] || '').toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{a.prenom} {a.nom}</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                          <span>login : {a.login}</span>
                          {a.telephone && <span>📱 {a.telephone}</span>}
                          {a.email && <span>✉ {a.email}</span>}
                          <span>Créé le {new Date(a.created_at).toLocaleDateString('fr-FR')}</span>
                        </div>
                      </div>
                      <RoleBadge name={getRoleName(a)} />
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => setModal({ type: 'editAccount', data: a })} title="Modifier">✏</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => setModal({ type: 'resetPwd', data: a })} title="Changer le mot de passe">🔑</button>
                        <button className="btn btn-danger btn-sm" onClick={() => setModal({ type: 'delAccount', data: a })} title="Supprimer">🗑</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          /* Onglet Rôles */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {roles.length === 0 ? (
              <div className="empty">
                <div className="icon">🏷</div>
                <p>Aucun rôle créé. Créez un rôle pour définir les accès par page.</p>
                <button className="btn btn-primary btn-sm" onClick={() => setModal({ type: 'createRole' })}>Créer le premier rôle</button>
              </div>
            ) : roles.map(r => {
              const nbAccounts = accounts.filter(a => (a.role_id || a.role) === r.id).length;
              return (
                <div key={r.id} className="card">
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>{r.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>
                        {r.pages.length} page(s) · {nbAccounts} compte(s) associé(s)
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => setModal({ type: 'editRole', data: r })}>✏ Modifier</button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => setModal({ type: 'delRole', data: r })}
                        disabled={nbAccounts > 0}
                        title={nbAccounts > 0 ? 'Réassignez les comptes avant de supprimer' : ''}
                      >🗑</button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {ALL_PAGES.map(p => (
                      <span key={p.path} style={{
                        fontSize: 11, padding: '3px 9px', borderRadius: 12,
                        fontWeight: r.pages.includes(p.path) ? 700 : 400,
                        background: r.pages.includes(p.path) ? 'rgba(59,130,246,0.12)' : 'var(--bg3)',
                        color: r.pages.includes(p.path) ? '#3b82f6' : 'var(--text3)',
                        border: `1px solid ${r.pages.includes(p.path) ? 'rgba(59,130,246,0.3)' : 'var(--border)'}`,
                      }}>
                        {r.pages.includes(p.path) ? '✓ ' : ''}{p.label}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modals */}
      {modal?.type === 'createAccount' && (
        <AccountModal roles={roles} onClose={closeModal} onSaved={() => saved('Compte créé')} onError={m => flash(m, true)} />
      )}
      {modal?.type === 'editAccount' && (
        <AccountModal account={modal.data} roles={roles} onClose={closeModal} onSaved={() => saved('Compte mis à jour')} onError={m => flash(m, true)} />
      )}
      {modal?.type === 'createRole' && (
        <RoleModal onClose={closeModal} onSaved={() => saved('Rôle créé')} onError={m => flash(m, true)} />
      )}
      {modal?.type === 'editRole' && (
        <RoleModal role={modal.data} onClose={closeModal} onSaved={() => saved('Rôle mis à jour')} onError={m => flash(m, true)} />
      )}
      {modal?.type === 'resetPwd' && (
        <ResetPasswordModal account={modal.data} onClose={closeModal} onDone={() => saved('Mot de passe modifié')} onError={m => flash(m, true)} />
      )}

      {modal?.type === 'delAccount' && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal" style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <div className="modal-title">Supprimer le compte</div>
              <button className="btn btn-ghost btn-sm" onClick={closeModal}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text2)', marginBottom: 12 }}>
                Supprimer le compte de <strong>{modal.data.prenom} {modal.data.nom}</strong> ? Cette action est irréversible.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>Annuler</button>
              <button className="btn btn-danger" onClick={async () => {
                try { await deleteAccount(modal.data.id); saved('Compte supprimé'); }
                catch { flash('Erreur suppression', true); closeModal(); }
              }}>Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {modal?.type === 'delRole' && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal" style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <div className="modal-title">Supprimer le rôle</div>
              <button className="btn btn-ghost btn-sm" onClick={closeModal}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text2)' }}>Supprimer le rôle <strong>{modal.data.name}</strong> ?</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>Annuler</button>
              <button className="btn btn-danger" onClick={async () => {
                try { await deleteRole(modal.data.id); saved('Rôle supprimé'); }
                catch { flash('Erreur suppression', true); closeModal(); }
              }}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
